/**
 * Login-Formular Komponente
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Avatar,
  Divider,
  Alert,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../hooks/useI18n';

export function LoginForm() {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, getErrorMessage } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState("");

  const handleGoogleLogin = async () => {
    setError("");
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validierung
    if (!email || !email.includes('@')) {
      setError(t.login.errorInvalidEmail);
      return;
    }
    
    if (isRegister) {
      if (password !== password2) {
        setError(t.login.errorPasswordMismatch);
        return;
      }
      if (password.length < 6) {
        setError(t.login.errorPasswordTooShort);
        return;
      }
    }
    
    try {
      if (isRegister) {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      
      // Wenn E-Mail bereits verwendet wird, wechsle automatisch zum Login-Modus
      if (err.code === 'auth/email-already-in-use' && isRegister) {
        setTimeout(() => {
          setIsRegister(false);
          setError(t.login.errorAlreadyRegistered);
        }, 1500);
      }
      
      // Spezielle Behandlung für Projekt-Mismatch-Fehler
      if (err.message && err.message.includes('falschen Firebase-Projekt')) {
        // Fehlermeldung ist bereits gesetzt, aber füge zusätzliche Hilfe hinzu
        console.log('Projekt-Mismatch erkannt - E-Mail ist im falschen Projekt registriert');
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#e8e8e8",
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d0d0d0' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "center",
        px: 2,
        pt: { xs: 0, sm: 2, md: 2 },
        pb: { xs: 1, sm: 2, md: 2 },
        boxSizing: "border-box",
      }}
    >
      <Card
        sx={{
          width: "100%",
          minWidth: 280,
          maxWidth: 400,
          p: { xs: 2.5, sm: 3 },
          boxShadow: 6,
          borderRadius: 3,
          mt: { xs: 0, md: 0 },
        }}
      >
        <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Avatar sx={{ m: { xs: 0.5, md: 1 }, bgcolor: "primary.main", width: 56, height: 56 }}>
            <LockOutlinedIcon fontSize="large" />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, mt: { xs: 0.5, md: 1 }, letterSpacing: 1 }}>
            {t.login.welcome}
          </Typography>
          
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleGoogleLogin}
            sx={{ mb: 2, textTransform: "none", fontWeight: 600, letterSpacing: 0.5 }}
          >
            {t.login.googleLogin}
          </Button>
          
          <Divider sx={{ width: "100%", my: 2 }}>{t.login.or}</Divider>
          
          <Box
            component="form"
            onSubmit={handleEmailLogin}
            sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
          >
            <TextField
              label={t.login.email}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              size="medium"
            />
            <TextField
              label={t.login.password}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              size="medium"
            />
            {isRegister && (
              <TextField
                label={t.login.passwordRepeat}
                type="password"
                value={password2}
                onChange={e => setPassword2(e.target.value)}
                required
                autoComplete="new-password"
                size="medium"
              />
            )}
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              fullWidth
              sx={{ fontWeight: 600, letterSpacing: 0.5 }}
            >
              {isRegister ? t.login.register : t.login.emailLogin}
            </Button>
          </Box>
          
          <Button
            onClick={() => setIsRegister(r => !r)}
            sx={{ mt: 2, textTransform: "none" }}
            color="inherit"
          >
            {isRegister ? t.login.alreadyRegistered : t.login.noAccount}
          </Button>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
